import { type CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BrandContainer } from "./BrandContainer";
import { ButtonContainer } from "./ButtonContainer";
import { ImageContainer } from "./Image";
import { ImageSelect } from "./ImageSelect";
import { InputContainer } from "./InputContainer";
import { getMeasurementTableViewportWidth, TableContainer } from "./TableContainer";
import isEqual from "lodash/isEqual";
import { SamplesContext } from "../../context/samplesContext";
import { UserContext } from "../../context/userContext";
import { ActionButtonsPanel } from "./ActionButtonsPanel";
import { SelectedImageInfo } from "./types";
import { ReadOnlyNoticeModal } from "../../components/ReadOnlyNoticeModal";
import { readSeenAdminNoticeSampleId, writeSeenAdminNoticeSampleId } from "./utils/adminNoticeStorage";
import { useTranslation } from "react-i18next";

export function Home() {
  const { t } = useTranslation()
  const { sample, rowSample, comparison, diff } = useContext(SamplesContext)
  const { isAuth, isGuest, isAdminMode } = useContext(UserContext)
  const [isUpdate, setIsUpdate] = useState<boolean>(false) // 過去サンプルも紐付けるかどうかのフラグ
  const [manualDrawingIndex, setManualDrawingIndex] = useState<number | null>(null) // テーブルで選択したIndex
  const [selectedImage, setSelectedImage] = useState<SelectedImageInfo | null>(null)
  const [isGuestNoticeOpen, setIsGuestNoticeOpen] = useState(false)
  const [hasShownGuestNotice, setHasShownGuestNotice] = useState(false)
  const [isAdminNoticeOpen, setIsAdminNoticeOpen] = useState(false)

  const sampleImageList = rowSample.sampleImages || []
  const ecImageList = rowSample.sampleEcImages || []
  const defaultSampleImage = useMemo(() => {
    if (sampleImageList.length === 0) return null
    const sorted = [...sampleImageList].sort((a, b) => a.image_index - b.image_index)
    return sorted[0]
  }, [sampleImageList])
  const comparisonColumns = useMemo(() => comparison?.columns ?? [], [comparison])
  const hasComparison = comparisonColumns.length > 0 && Boolean(comparison?.rows?.length)
  const currentColumnKey = useMemo(() => {
    const currentColumn = comparisonColumns.find(column => column.is_current || column.sample_id === rowSample.id)
    return currentColumn?.key ?? null
  }, [comparisonColumns, rowSample.id])
  const tableViewportWidth = useMemo(() => {
    return getMeasurementTableViewportWidth({
      comparisonColumns,
      currentColumnKey,
      hasComparison,
      hasSpec: Boolean(diff?.data?.condition.hasSpec),
    })
  }, [comparisonColumns, currentColumnKey, diff?.data?.condition.hasSpec, hasComparison])
  const tableAreaStyle = useMemo<CSSProperties & Record<'--measurement-table-max-width', string>>(() => ({
    '--measurement-table-max-width': `${tableViewportWidth}px`,
  }), [tableViewportWidth])

  useEffect(() => {
    if (!sample || !rowSample) return

    const hasChanged = !isEqual(sample, rowSample)

    if (hasChanged) {
      const handleBeforeUnload = (event: any) => {
        event.preventDefault()
        event.returnValue = ''
      }

      window.addEventListener('beforeunload', handleBeforeUnload)

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload)
      }
    }
  }, [sample, rowSample])

  useEffect(() => {
    setSelectedImage(prev => {
      if (defaultSampleImage) {
        return { source: 'sample', image: defaultSampleImage }
      }
      if (ecImageList.length > 0) {
        const sortedEc = [...ecImageList].sort((a, b) => a.image_index - b.image_index)
        return { source: 'ec', image: sortedEc[0] }
      }
      return null
    })
  }, [defaultSampleImage, sampleImageList, ecImageList])

  useEffect(() => {
    if (manualDrawingIndex === null) return
    const targetMeasurement = rowSample.sampleMeasurements.find(
      m => m.measure_index === manualDrawingIndex && !m.is_deleted
    )
    if (targetMeasurement) {
      const matchedImage = rowSample.sampleImages.find(img => img.image_index === targetMeasurement.image_index)
      if (!matchedImage) return
      setSelectedImage(prev => {
        if (prev?.source === 'sample' && prev.image.image_index === matchedImage.image_index) {
          return prev
        }
        return { source: 'sample', image: matchedImage }
      })
      return
    }
    if (defaultSampleImage && selectedImage?.source !== 'sample') {
      setSelectedImage({ source: 'sample', image: defaultSampleImage })
    }
  }, [manualDrawingIndex, rowSample.sampleMeasurements, rowSample.sampleImages, selectedImage, defaultSampleImage])

  useEffect(() => {
    if (!isAuth || !isGuest || hasShownGuestNotice) return
    setIsGuestNoticeOpen(true)
    setHasShownGuestNotice(true)
  }, [isAuth, isGuest, hasShownGuestNotice])

  useEffect(() => {
    if (!isAuth || isGuest || !isAdminMode) return
    const currentSampleId = rowSample?.id
    if (!currentSampleId) return

    const seenSampleId = readSeenAdminNoticeSampleId()
    if (seenSampleId === currentSampleId) return

    setIsAdminNoticeOpen(true)
  }, [isAuth, isGuest, isAdminMode, rowSample?.id])

  const handleAdminNoticeClose = useCallback(() => {
    const currentSampleId = rowSample?.id
    if (currentSampleId) {
      writeSeenAdminNoticeSampleId(currentSampleId)
    }
    setIsAdminNoticeOpen(false)
  }, [rowSample?.id])

  const handleSelectImage = useCallback((source: SelectedImageInfo['source'], image: SelectedImageInfo['image']) => {
    setSelectedImage(prev => {
      if (prev?.source === source && prev.image.image_index === image.image_index) {
        return prev
      }
      return { source, image }
    })
  }, [])

  return (
    <div className="flex flex-col justify-center mt-[12px] mb-[38px] mx-[20px] min-w-[728px] desktop-xmd:mx-[71.5px] desktop-xmd:flex-row">
      <div className="flex flex-col w-full min-w-[250px] mb-[16px] gap-[16px] desktop-xmd:max-w-[250px] desktop-xmd:gap-0 desktop-xmd:mb-0 desktop-xmd:order-3 desktop-xmd:ml-[13px] desktop-xmd:justify-between">
        <InputContainer setIsUpdate={setIsUpdate} />
        <div className="hidden desktop-xmd:block">
          <BrandContainer />
        </div>
        <div className="hidden desktop-xmd:block">
          <ButtonContainer isUpdate={isUpdate} setIsUpdate={setIsUpdate} />
        </div>
      </div>
      <div className="flex flex-col w-full desktop-xmd:order-1 desktop-xmd:mr-[10px] desktop-xmd:max-w-[1000px] desktop-xmd:min-w-[600px]">
        <ImageContainer
          setManualDrawingIndex={setManualDrawingIndex}
          manualDrawingIndex={manualDrawingIndex}
          selectedImage={selectedImage}
        />
        <ImageSelect selectedImage={selectedImage} onSelectImage={handleSelectImage} />
      </div>
      <div
        className="flex flex-col w-full min-w-[453px] desktop-xmd:order-2 desktop-xmd:flex-1 desktop-xmd:min-w-[473px] desktop-xmd:max-w-[var(--measurement-table-max-width)]"
        style={tableAreaStyle}
      >
        <div className="flex w-full min-w-[453px] flex-row gap-[12px] mt-[5px] mb-[30px] desktop-xmd:flex-col desktop-xmd:mt-0 desktop-xmd:mb-0 desktop-xmd:min-w-[473px] desktop-xmd:max-w-[var(--measurement-table-max-width)]">
          <div className="min-w-[453px] flex-1 desktop-xmd:min-w-[473px] desktop-xmd:max-w-[var(--measurement-table-max-width)]">
            <TableContainer
              setManualDrawingIndex={setManualDrawingIndex}
              manualDrawingIndex={manualDrawingIndex}
              selectedImage={selectedImage}
            />
          </div>
          <div className="flex max-w-[272px] min-w-[226px] shrink-0 flex-col desktop-xmd:hidden">
            <ActionButtonsPanel />
            <BrandContainer />
            <ButtonContainer isUpdate={isUpdate} setIsUpdate={setIsUpdate} />
          </div>
        </div>
        <div className="hidden desktop-xmd:block mt-[12px] desktop-xmd:w-[473px] desktop-xmd:max-w-[473px] desktop-xmd:self-end">
          <ActionButtonsPanel />
        </div>
      </div>
      <ReadOnlyNoticeModal
        isOpen={isGuestNoticeOpen}
        onClose={() => setIsGuestNoticeOpen(false)}
        message={t('readonly.otherDepartment')}
      />
      <ReadOnlyNoticeModal
        isOpen={isAdminNoticeOpen}
        onClose={handleAdminNoticeClose}
        message={t('readonly.adminMode')}
      />
    </div>
  )
}
